import React, { useEffect, useRef, useState } from "react";
import { Lightbulb, Plus, Trash2, Upload, Layers, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const PLATFORMS = [
  { value: "desty", label: "Desty (Semua Channel)" },
  { value: "shopee", label: "Shopee" },
  { value: "tiktok", label: "TikTok Shop" },
  { value: "tokopedia", label: "Tokopedia" },
  { value: "lazada", label: "Lazada" },
];

const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const emptyComponent = () => ({ variant_id: "", label: "", qty: 1, price: "" });

const SUG_STATUS = {
  matched: { label: "Cocok Pasti", cls: "bg-status-passed/15 text-status-passed" },
  review: { label: "Perlu Review", cls: "bg-amber-100 text-amber-800" },
  conflict: { label: "Konflik Data", cls: "bg-red-100 text-red-700" },
  variant_not_found: { label: "Varian Tidak Ada", cls: "bg-orange-100 text-orange-700" },
  product_not_found: { label: "Produk Tidak Ada", cls: "bg-taupe/25 text-cocoa" },
  incomplete: { label: "Data Kurang", cls: "bg-taupe/20 text-cocoa" },
  unanalyzed: { label: "Belum Dianalisis", cls: "bg-taupe/10 text-wmuted" },
};
const sugMeta = (s) => SUG_STATUS[s || "unanalyzed"] || SUG_STATUS.unanalyzed;
const SUG_FILTERS = ["matched", "review", "conflict", "variant_not_found", "product_not_found", "unanalyzed"];

function VariantPicker({ label, onSelect, testid, inline = false }) {
  const [q, setQ] = useState(label || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => { setQ(label || ""); }, [label]);

  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/products?q=${encodeURIComponent(val)}`);
      const products = Array.isArray(res.data) ? res.data : (res.data.rows || res.data.products || []);
      const flat = [];
      products.forEach((p) => (p.variants || []).forEach((v) => flat.push({
        variant_id: v.id,
        product: p.name,
        detail: `${v.color || ""}/${v.size || ""}`.replace(/^\/|\/$/g, ""),
        sku: v.sku || "",
        label: `${p.name} ${v.color || ""}/${v.size || ""} (${v.sku || "-"})`.trim(),
      })));
      setResults(flat.slice(0, 100));
    } catch { setResults([]); } finally { setSearching(false); }
  };

  const listCls = inline
    ? "mt-1 max-h-72 w-full overflow-auto rounded-md border border-taupe/50 bg-ivory"
    : "absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border border-taupe/50 bg-ivory shadow-xl";

  return (
    <div className="relative">
      <Input data-testid={testid} onChange={(e) => search(e.target.value)} placeholder="Cari produk / warna / SKU ERP..." value={q} />
      {searching && <div className="mt-1 text-xs text-wmuted">Mencari...</div>}
      {results.length > 0 && (
        <div className={listCls}>
          <div className="sticky top-0 border-b border-taupe/30 bg-taupe/10 px-3 py-1 text-xs text-wmuted">{results.length} varian ditemukan</div>
          {results.map((v) => (
            <button
              className="flex w-full flex-col items-start gap-0.5 border-b border-taupe/15 px-3 py-2 text-left hover:bg-cocoa/10"
              data-testid={`${testid}-opt-${v.variant_id}`}
              key={v.variant_id}
              onClick={() => { onSelect(v.variant_id, v.label); setQ(v.label); setResults([]); }}
              type="button"
            >
              <span className="text-sm font-medium text-cocoa">{v.product}</span>
              <span className="text-xs text-wmuted">{v.detail || "—"}{v.sku ? ` · SKU ${v.sku}` : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VariantMapping() {
  const { canEdit } = useAuth();
  const canWrite = canEdit("variant_mapping");
  const fileRef = useRef(null);

  const [platform, setPlatform] = useState("desty");
  const [mappings, setMappings] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [form, setForm] = useState({ marketplace_sku: "", components: [emptyComponent()] });

  const [candidates, setCandidates] = useState([]);
  const [candTotals, setCandTotals] = useState({ total: 0, unmapped_total: 0 });
  const [candSummary, setCandSummary] = useState({});
  const [candQuery, setCandQuery] = useState("");
  const [candStatus, setCandStatus] = useState("unmapped");
  const [candSStatus, setCandSStatus] = useState("");
  const [importing, setImporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [candPage, setCandPage] = useState(1);
  const [candMeta, setCandMeta] = useState({ filtered_total: 0, pages: 1 });
  const [mapDialog, setMapDialog] = useState({ open: false, candidate: null, options: [], selectedId: "", selectedLabel: "" });
  const [mapQuery, setMapQuery] = useState("");
  const [mapKind, setMapKind] = useState("all");
  const [mapPage, setMapPage] = useState(1);
  const [mapMeta, setMapMeta] = useState({ total: 0, pages: 1 });

  const isBundle = form.components.length > 1;

  const loadMappings = async (opts = {}) => {
    const page = opts.page ?? mapPage;
    const kind = opts.kind ?? mapKind;
    const q = opts.q ?? mapQuery;
    try {
      const res = await api.get("/marketplace-shipping/variant-mappings", {
        params: { platform, q, kind, page, limit: 50 },
      });
      setMappings(res.data.mappings || []);
      setMapMeta({ total: res.data.total || 0, pages: res.data.pages || 1 });
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const load = async () => {
    try {
      const batchRes = await api.get("/marketplace-shipping/batches");
      setBatches((batchRes.data || []).filter((b) => b.marketplace === platform));
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
    setMapPage(1);
    await loadMappings({ page: 1 });
  };

  const loadCandidates = async (opts = {}) => {
    const page = opts.page ?? candPage;
    const status = opts.status ?? candStatus;
    const q = opts.q ?? candQuery;
    const sstatus = opts.sstatus ?? candSStatus;
    try {
      const res = await api.get("/marketplace-shipping/variant-mappings/candidates", {
        params: { status, q, sstatus, page, limit: 50 },
      });
      setCandidates(res.data.candidates || []);
      setCandTotals({ total: res.data.total || 0, unmapped_total: res.data.unmapped_total || 0 });
      setCandMeta({ filtered_total: res.data.filtered_total || 0, pages: res.data.pages || 1 });
      setCandSummary(res.data.summary || {});
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    setSuggestions([]);
    setBatchId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  useEffect(() => {
    loadCandidates({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analyzeFilter = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post("/marketplace-shipping/variant-mappings/candidates/analyze", null, {
        params: { q: candQuery, status: candStatus, sstatus: candSStatus },
      });
      const c = res.data.counts || {};
      toast.success(`Analisis ${res.data.analyzed} SKU · cocok ${c.matched || 0}, review ${c.review || 0}, konflik ${c.conflict || 0}`);
      setCandPage(1);
      await loadCandidates({ page: 1 });
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setAnalyzing(false);
    }
  };

  const acceptSuggestion = async (candidateId) => {
    setApproving(true);
    try {
      const res = await api.post("/marketplace-shipping/variant-mappings/candidates/approve", { candidate_ids: [candidateId] });
      if (res.data.applied) toast.success("Saran diterima & mapping disimpan");
      else toast.info("Tidak tersimpan (sudah dipetakan atau bukan cocok pasti)");
      await load();
      await loadCandidates();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setApproving(false);
    }
  };

  const approveMatched = async () => {
    setApproving(true);
    try {
      const dry = await api.post("/marketplace-shipping/variant-mappings/candidates/approve", { scope: "filtered", q: candQuery, status: candStatus, dry_run: true });
      if (!dry.data.count) { toast.info("Tidak ada kandidat 'Cocok Pasti' pada filter ini"); return; }
      if (!window.confirm(`Setujui ${dry.data.count} mapping 'Cocok Pasti'?\nContoh: ${(dry.data.preview || []).slice(0, 3).map((p) => p.master_sku).join(", ")}`)) return;
      const res = await api.post("/marketplace-shipping/variant-mappings/candidates/approve", { scope: "filtered", q: candQuery, status: candStatus });
      toast.success(`${res.data.applied} mapping disimpan (${res.data.skipped} dilewati)`);
      await load();
      await loadCandidates();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setApproving(false);
    }
  };

  const setComponent = (idx, patch) => setForm((f) => ({
    ...f, components: f.components.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
  }));
  const addComponent = () => setForm((f) => ({ ...f, components: [...f.components, emptyComponent()] }));
  const removeComponent = (idx) => setForm((f) => ({
    ...f, components: f.components.length > 1 ? f.components.filter((_, i) => i !== idx) : f.components,
  }));
  const resetForm = () => setForm({ marketplace_sku: "", components: [emptyComponent()] });

  const save = async () => {
    if (!form.marketplace_sku.trim()) { toast.error("SKU marketplace wajib diisi"); return; }
    if (form.components.some((c) => !c.variant_id)) { toast.error("Semua komponen wajib pilih varian ERP"); return; }
    try {
      await api.post("/marketplace-shipping/variant-mappings", {
        platform,
        marketplace_sku: form.marketplace_sku.trim(),
        components: form.components.map((c) => ({
          variant_id: c.variant_id,
          qty: Number(c.qty) || 1,
          price: c.price === "" || c.price === null ? null : Number(c.price),
        })),
      });
      toast.success(isBundle ? "Mapping Couple/Bundle disimpan" : "Mapping varian disimpan");
      resetForm();
      await load();
      await loadCandidates();
      if (batchId) await loadSuggestions();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const createSingle = async (sku, variantId) => {
    try {
      await api.post("/marketplace-shipping/variant-mappings", {
        platform, marketplace_sku: sku, components: [{ variant_id: variantId, qty: 1 }],
      });
      toast.success("Mapping varian disimpan");
      await load();
      await loadCandidates();
      if (batchId) await loadSuggestions();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/marketplace-shipping/variant-mappings/${id}`);
      await load();
      await loadCandidates();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const loadSuggestions = async () => {
    if (!batchId) return;
    try {
      const res = await api.get(`/marketplace-shipping/batches/${batchId}/variant-suggestions`);
      setSuggestions(res.data.suggestions || []);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const bulkApply = async () => {
    const targets = suggestions.filter((s) => s.confidence === "high" && s.suggested_variant_id && s.marketplace_sku);
    if (targets.length === 0) { toast.info("Tidak ada saran keyakinan tinggi ber-SKU untuk diterapkan"); return; }
    let ok = 0;
    for (const s of targets) {
      try {
        await api.post("/marketplace-shipping/variant-mappings", {
          platform, marketplace_sku: s.marketplace_sku, components: [{ variant_id: s.suggested_variant_id, qty: 1 }],
        });
        ok += 1;
      } catch { /* skip gagal */ }
    }
    toast.success(`${ok}/${targets.length} mapping diterapkan`);
    await load();
    await loadCandidates();
    await loadSuggestions();
  };

  const refreshDesty = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/marketplace-shipping/variant-mappings/refresh-desty");
      toast.success(`Refresh Desty: ${res.data.total} SKU (${res.data.created} baru, ${res.data.updated} diperbarui)`);
      await loadCandidates({ page: 1 });
      setCandPage(1);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const onImportFile = async (e) => {    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/marketplace-shipping/variant-mappings/import-desty", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Import Desty: ${res.data.total} SKU (${res.data.created} baru, ${res.data.updated} diperbarui)`);
      await loadCandidates();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const mapCandidate = (c) => {
    const opts = (c.suggestion_alternatives && c.suggestion_alternatives.length)
      ? c.suggestion_alternatives
      : (c.suggested_variant_id ? [{ variant_id: c.suggested_variant_id, label: c.suggested_label }] : []);
    setMapDialog({ open: true, candidate: c, options: opts, selectedId: "", selectedLabel: "" });
  };

  const saveMapDialog = async () => {
    const { candidate, selectedId } = mapDialog;
    if (!selectedId) { toast.error("Pilih varian ERP dulu"); return; }
    try {
      await api.post("/marketplace-shipping/variant-mappings", {
        platform: "desty", marketplace_sku: candidate.master_sku, components: [{ variant_id: selectedId, qty: 1 }],
      });
      toast.success("Mapping disimpan");
      setMapDialog({ open: false, candidate: null, options: [], selectedId: "", selectedLabel: "" });
      await load();
      await loadCandidates();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div data-testid="variant-mapping-page">
      <PageHeader
        title="Mapping Varian Marketplace"
        subtitle="Petakan SKU marketplace ke varian ERP (termasuk Couple/Bundle 1 SKU = beberapa varian) untuk verifikasi isi order"
      />
      <div className="space-y-5">
        <Panel className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Platform</Label>
              <Select onValueChange={setPlatform} value={platform}>
                <SelectTrigger className="w-56" data-testid="variant-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {canWrite && (
              <div>
                <Label className="block">Import Listing Desty</Label>
                <div className="flex gap-2">
                  <input accept=".xlsx" className="hidden" data-testid="desty-import-input" onChange={onImportFile} ref={fileRef} type="file" />
                  <Button data-testid="desty-import-btn" disabled={importing} onClick={() => fileRef.current?.click()} variant="outline">
                    <Upload className="mr-1 h-4 w-4" />{importing ? "Mengimpor..." : "Upload .xlsx"}
                  </Button>
                  <Button className="bg-cocoa text-ivory hover:bg-cocoa-dark" data-testid="desty-refresh-btn" disabled={refreshing} onClick={refreshDesty}>
                    <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />{refreshing ? "Menarik..." : "Refresh dari Desty"}
                  </Button>
                </div>
              </div>
            )}
            <div className="min-w-[220px]">
              <Label>Saran dari Batch</Label>
              <Select onValueChange={setBatchId} value={batchId}>
                <SelectTrigger data-testid="variant-batch-select"><SelectValue placeholder="Pilih batch" /></SelectTrigger>
                <SelectContent>
                  {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.filename} · {b.start}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="variant-load-suggestions" disabled={!batchId} onClick={loadSuggestions} variant="outline">
              <Lightbulb className="mr-1 h-4 w-4" />Muat Saran
            </Button>
          </div>
        </Panel>

        {/* Kandidat dari import Desty */}
        <Panel className="p-4" data-testid="candidates-panel">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-cocoa">Kandidat SKU (Listing Desty)</span>
            <span className="text-xs text-wmuted">Belum di-mapping: <b>{candTotals.unmapped_total}</b> · Total: {candTotals.total}</span>
          </div>
          {/* Ringkasan status saran */}
          <div className="mb-3 flex flex-wrap gap-2" data-testid="candidate-summary">
            <button
              className={`rounded-full border px-3 py-1 text-xs ${candSStatus === "" ? "border-cocoa bg-cocoa text-ivory" : "border-taupe/40 text-cocoa"}`}
              data-testid="sfilter-semua"
              onClick={() => { setCandSStatus(""); setCandPage(1); loadCandidates({ page: 1, sstatus: "" }); }}
              type="button"
            >Semua ({candMeta.filtered_total})</button>
            {SUG_FILTERS.map((s) => (
              <button
                className={`rounded-full border px-3 py-1 text-xs ${candSStatus === s ? "border-cocoa bg-cocoa text-ivory" : "border-taupe/40 text-cocoa"}`}
                data-testid={`sfilter-${s}`}
                key={s}
                onClick={() => { setCandSStatus(s); setCandPage(1); loadCandidates({ page: 1, sstatus: s }); }}
                type="button"
              >{sugMeta(s).label} ({candSummary[s] || 0})</button>
            ))}
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Input
              className="max-w-xs"
              data-testid="candidate-search"
              onChange={(e) => setCandQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (setCandPage(1), loadCandidates({ page: 1 }))}
              placeholder="Cari SKU / produk (mis. SAMARA)..."
              value={candQuery}
            />
            <Button data-testid="candidate-search-btn" onClick={() => { setCandPage(1); loadCandidates({ page: 1 }); }} size="sm" variant="outline">Cari Kandidat</Button>
            {canWrite && (
              <>
                <Button data-testid="candidate-analyze-btn" disabled={analyzing} onClick={analyzeFilter} size="sm" variant="outline">
                  <Lightbulb className="mr-1 h-4 w-4" />{analyzing ? "Menganalisis..." : "Analisis Filter"}
                </Button>
                <Button className="bg-cocoa text-ivory hover:bg-cocoa-dark" data-testid="candidate-approve-btn" disabled={approving} onClick={approveMatched} size="sm">
                  {approving ? "Menyimpan..." : "Setujui Cocok Pasti"}
                </Button>
              </>
            )}
            <div className="ml-auto flex gap-1">
              <Button data-testid="candidate-filter-unmapped" onClick={() => { setCandQuery(""); setCandStatus("unmapped"); setCandPage(1); loadCandidates({ page: 1, status: "unmapped", q: "" }); }} size="sm" variant={candStatus === "unmapped" ? "default" : "outline"}>Belum di-mapping</Button>
              <Button data-testid="candidate-filter-all" onClick={() => { setCandQuery(""); setCandStatus("all"); setCandPage(1); loadCandidates({ page: 1, status: "all", q: "" }); }} size="sm" variant={candStatus === "all" ? "default" : "outline"}>Semua</Button>
            </div>
          </div>
          <div className="max-h-[28rem] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Master SKU</TableHead>
                  <TableHead>Variasi Marketplace</TableHead>
                  <TableHead>Saran Produk ERP</TableHead>
                  <TableHead>Status / Alasan</TableHead>
                  <TableHead>Mapping</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.length === 0 && (
                  <TableRow><TableCell className="text-center text-wmuted" colSpan={6}>Belum ada kandidat. Upload file Master Product Desty.</TableCell></TableRow>
                )}
                {candidates.map((c) => {
                  const sm = sugMeta(c.suggestion_status);
                  const alts = c.suggestion_alternatives || [];
                  return (
                    <TableRow data-testid={`candidate-row-${c.id}`} key={c.id}>
                      <TableCell className="font-mono text-xs">{c.master_sku}</TableCell>
                      <TableCell className="text-wmuted">{c.variant_name || c.product_name}</TableCell>
                      <TableCell>{c.suggested_label || <span className="text-wmuted">—</span>}</TableCell>
                      <TableCell>
                        <Badge className={`border-0 ${sm.cls}`}>{sm.label}</Badge>
                        {c.suggestion_reason && <div className="mt-1 text-xs text-wmuted">{c.suggestion_reason}</div>}
                        {alts.length > 1 && (
                          <button
                            className="mt-1 text-xs text-cocoa underline"
                            data-testid={`candidate-alts-${c.id}`}
                            onClick={() => window.alert("Alternatif:\n" + alts.map((a) => "• " + a.label).join("\n"))}
                            type="button"
                          >{alts.length} alternatif</button>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.mapped
                          ? <Badge className="border-0 bg-status-passed/15 text-status-passed">Ter-mapping</Badge>
                          : <Badge className="border-0 bg-amber-100 text-amber-800">Belum</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        {canWrite && !c.mapped && c.suggestion_status === "matched" && c.suggested_variant_id && (
                          <Button className="mr-1" data-testid={`candidate-accept-${c.id}`} disabled={approving} onClick={() => acceptSuggestion(c.id)} size="sm" variant="outline">Terima</Button>
                        )}
                        {canWrite && !c.mapped && (
                          <Button data-testid={`candidate-map-${c.id}`} onClick={() => mapCandidate(c)} size="sm" variant="ghost">Petakan</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {candMeta.pages > 1 && (
            <div className="mt-3 flex items-center justify-between text-sm" data-testid="candidate-pagination">
              <span className="text-wmuted">Hal {candPage} / {candMeta.pages} · {candMeta.filtered_total} item</span>
              <div className="flex gap-1">
                <Button data-testid="candidate-prev" disabled={candPage <= 1} onClick={() => { const p = candPage - 1; setCandPage(p); loadCandidates({ page: p }); }} size="sm" variant="outline">Sebelumnya</Button>
                <Button data-testid="candidate-next" disabled={candPage >= candMeta.pages} onClick={() => { const p = candPage + 1; setCandPage(p); loadCandidates({ page: p }); }} size="sm" variant="outline">Berikutnya</Button>
              </div>
            </div>
          )}
        </Panel>

        {suggestions.length > 0 && (
          <Panel className="p-4" data-testid="variant-suggestions">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-semibold text-cocoa">Saran Mapping Otomatis</span>
              {canWrite && (
                <Button data-testid="variant-apply-all" onClick={bulkApply} size="sm" variant="outline">Terapkan Semua (Keyakinan Tinggi)</Button>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU / Varian File</TableHead>
                  <TableHead>Produk (file)</TableHead>
                  <TableHead>Saran Varian ERP</TableHead>
                  <TableHead>Keyakinan</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.map((s, idx) => (
                  <TableRow data-testid={`variant-suggestion-${idx}`} key={(s.marketplace_sku || s.name) + idx}>
                    <TableCell className="font-mono text-xs">{s.marketplace_sku || s.marketplace_variation || "-"}</TableCell>
                    <TableCell>{s.name} {s.color}/{s.size}</TableCell>
                    <TableCell>
                      {s.suggested_label
                        ? <span>{s.suggested_label}<span className="ml-1 text-xs text-wmuted">· {s.reason}</span></span>
                        : <span className="text-status-review">Tidak ada saran</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={`border-0 ${s.confidence === "high" ? "bg-status-passed/15 text-status-passed" : s.confidence === "medium" ? "bg-amber-100 text-amber-800" : "bg-taupe/20 text-cocoa"}`}>
                        {s.confidence === "high" ? "Tinggi" : s.confidence === "medium" ? "Sedang" : "Tidak ada"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {canWrite && s.suggested_variant_id && s.marketplace_sku && (
                        <Button data-testid={`variant-apply-${idx}`} onClick={() => createSingle(s.marketplace_sku, s.suggested_variant_id)} size="sm" variant="outline">Terapkan</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Panel>
        )}

        {canWrite && (
          <Panel className="relative z-20 overflow-visible p-4" data-testid="variant-mapping-form">
            <div className="mb-3 flex items-center gap-2 font-semibold text-cocoa">
              <Layers className="h-4 w-4" />Tambah / Ubah Mapping {isBundle && <Badge className="border-0 bg-cocoa/10 text-cocoa">Couple/Bundle</Badge>}
            </div>
            <div className="mb-3 max-w-xs">
              <Label>SKU Marketplace</Label>
              <Input
                data-testid="variant-sku-input"
                onChange={(e) => setForm((f) => ({ ...f, marketplace_sku: e.target.value }))}
                placeholder="SKU dari listing"
                value={form.marketplace_sku}
              />
            </div>
            <div className="space-y-2">
              <Label>Komponen Varian ERP {isBundle && <span className="text-xs text-wmuted">(1 SKU = {form.components.length} varian)</span>}</Label>
              {form.components.map((c, idx) => (
                <div className="grid gap-2 md:grid-cols-[minmax(240px,1fr)_90px_130px_auto] md:items-end" data-testid={`component-row-${idx}`} key={idx}>
                  <VariantPicker
                    label={c.label}
                    onSelect={(vid, lbl) => setComponent(idx, { variant_id: vid, label: lbl })}
                    testid={`component-variant-${idx}`}
                  />
                  <div>
                    <Label className="text-xs text-wmuted">Qty</Label>
                    <Input data-testid={`component-qty-${idx}`} min={1} onChange={(e) => setComponent(idx, { qty: e.target.value })} type="number" value={c.qty} />
                  </div>
                  <div>
                    <Label className="text-xs text-wmuted">Harga (opsional)</Label>
                    <Input data-testid={`component-price-${idx}`} onChange={(e) => setComponent(idx, { price: e.target.value })} placeholder="default" type="number" value={c.price} />
                  </div>
                  <Button data-testid={`component-remove-${idx}`} disabled={form.components.length === 1} onClick={() => removeComponent(idx)} size="icon" variant="ghost">
                    <Trash2 className="h-4 w-4 text-status-reject" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button data-testid="add-component-btn" onClick={addComponent} size="sm" variant="outline">
                <Plus className="mr-1 h-4 w-4" />Tambah Komponen (Couple)
              </Button>
              <Button className="bg-cocoa text-ivory hover:bg-cocoa-dark" data-testid="variant-mapping-save" onClick={save}>
                Simpan Mapping
              </Button>
            </div>
          </Panel>
        )}

        <Panel className="p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="font-semibold text-cocoa">Mapping Tersimpan</span>
            <span className="text-xs text-wmuted">({mapMeta.total})</span>
            <Input
              className="ml-2 max-w-xs"
              data-testid="mapping-search"
              onChange={(e) => setMapQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (setMapPage(1), loadMappings({ page: 1 }))}
              placeholder="Cari SKU / produk..."
              value={mapQuery}
            />
            <Button data-testid="mapping-search-btn" onClick={() => { setMapPage(1); loadMappings({ page: 1 }); }} size="sm" variant="outline">Cari Mapping</Button>
            <div className="ml-auto flex gap-1">
              <Button data-testid="mapping-filter-all" onClick={() => { setMapKind("all"); setMapPage(1); loadMappings({ page: 1, kind: "all" }); }} size="sm" variant={mapKind === "all" ? "default" : "outline"}>Semua</Button>
              <Button data-testid="mapping-filter-single" onClick={() => { setMapKind("single"); setMapPage(1); loadMappings({ page: 1, kind: "single" }); }} size="sm" variant={mapKind === "single" ? "default" : "outline"}>Tunggal</Button>
              <Button data-testid="mapping-filter-bundle" onClick={() => { setMapKind("bundle"); setMapPage(1); loadMappings({ page: 1, kind: "bundle" }); }} size="sm" variant={mapKind === "bundle" ? "default" : "outline"}>Couple</Button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU Marketplace</TableHead>
                <TableHead>Varian ERP</TableHead>
                <TableHead>Tipe</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.length === 0 && (
                <TableRow><TableCell className="text-center text-wmuted" colSpan={4}>Belum ada mapping varian</TableCell></TableRow>
              )}
              {mappings.map((m) => (
                <TableRow data-testid={`variant-mapping-row-${m.id}`} key={m.id}>
                  <TableCell className="font-mono text-xs">{m.marketplace_sku}</TableCell>
                  <TableCell>
                    {m.is_bundle
                      ? (m.components || []).map((c, i) => (
                        <div className="text-sm" key={i}>
                          {c.product_name} {c.color}/{c.size} <span className="text-wmuted">×{c.qty || 1}{c.price != null ? ` · ${rupiah(c.price)}` : ""}</span>
                        </div>
                      ))
                      : <span>{m.product_name} {m.color}/{m.size} <span className="text-wmuted">({m.erp_sku || "-"})</span></span>}
                  </TableCell>
                  <TableCell>
                    {m.is_bundle
                      ? <Badge className="border-0 bg-cocoa/10 text-cocoa">Couple ({(m.components || []).length})</Badge>
                      : <Badge className="border-0 bg-taupe/20 text-cocoa">Tunggal</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    {canWrite && (
                      <Button data-testid={`variant-mapping-delete-${m.id}`} onClick={() => remove(m.id)} size="sm" variant="ghost">
                        <Trash2 className="h-4 w-4 text-status-reject" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {mapMeta.pages > 1 && (
            <div className="mt-3 flex items-center justify-between text-sm" data-testid="mapping-pagination">
              <span className="text-wmuted">Hal {mapPage} / {mapMeta.pages} · {mapMeta.total} item</span>
              <div className="flex gap-1">
                <Button data-testid="mapping-prev" disabled={mapPage <= 1} onClick={() => { const p = mapPage - 1; setMapPage(p); loadMappings({ page: p }); }} size="sm" variant="outline">Sebelumnya</Button>
                <Button data-testid="mapping-next" disabled={mapPage >= mapMeta.pages} onClick={() => { const p = mapPage + 1; setMapPage(p); loadMappings({ page: p }); }} size="sm" variant="outline">Berikutnya</Button>
              </div>
            </div>
          )}
        </Panel>
      </div>

      <Dialog onOpenChange={(o) => !o && setMapDialog((d) => ({ ...d, open: false }))} open={mapDialog.open}>
        <DialogContent className="max-w-lg" data-testid="map-dialog">
          <DialogHeader>
            <DialogTitle>Petakan SKU ke Varian ERP</DialogTitle>
            <DialogDescription>Pilih dari saran otomatis atau cari varian ERP lain untuk SKU ini.</DialogDescription>
          </DialogHeader>
          {mapDialog.candidate && (
            <div className="space-y-4">
              <div className="rounded-md bg-taupe/10 p-3 text-sm">
                <div className="font-mono text-xs text-cocoa">{mapDialog.candidate.master_sku}</div>
                <div className="text-wmuted">{mapDialog.candidate.variant_name || mapDialog.candidate.product_name}</div>
              </div>

              {mapDialog.options.length > 0 && (
                <div>
                  <Label className="mb-1 block">Pilih dari saran ({mapDialog.options.length})</Label>
                  <div className="space-y-1">
                    {mapDialog.options.map((o) => (
                      <button
                        className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm ${mapDialog.selectedId === o.variant_id ? "border-cocoa bg-cocoa/10" : "border-taupe/40 hover:bg-taupe/10"}`}
                        data-testid={`map-option-${o.variant_id}`}
                        key={o.variant_id}
                        onClick={() => setMapDialog((d) => ({ ...d, selectedId: o.variant_id, selectedLabel: o.label }))}
                        type="button"
                      >
                        <span>{o.label}</span>
                        {mapDialog.selectedId === o.variant_id && <Badge className="border-0 bg-status-passed/15 text-status-passed">Dipilih</Badge>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="mb-1 block">{mapDialog.options.length > 0 ? "Atau cari produk lain" : "Cari varian ERP"}</Label>
                <VariantPicker
                  inline
                  label=""
                  onSelect={(vid, lbl) => setMapDialog((d) => ({ ...d, selectedId: vid, selectedLabel: lbl }))}
                  testid="map-dialog-search"
                />
              </div>

              {mapDialog.selectedLabel && (
                <div className="rounded-md border border-cocoa/30 bg-cocoa/5 p-2 text-sm" data-testid="map-selected">
                  Terpilih: <b>{mapDialog.selectedLabel}</b>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setMapDialog((d) => ({ ...d, open: false }))} variant="outline">Batal</Button>
            <Button className="bg-cocoa text-ivory hover:bg-cocoa-dark" data-testid="map-dialog-save" disabled={!mapDialog.selectedId} onClick={saveMapDialog}>Simpan Mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
